import { CarrierVisitPartialWorkAreaDto } from '@operations/app/api'
import { extractSubFormData, FieldStore, ISubFormStore, validateSubForm } from '@tom-ui/utils'
import { action, computed, makeObservable, observable } from 'mobx'
import {
  ICarrierVisitAssignment,
  ICarrierVisitAssignmentFormData,
} from '../models/operational-equipment-assignment.model'

export class CarrierVisitAssignmentSubFormStore
  implements ISubFormStore<ICarrierVisitAssignmentFormData>
{
  assignments = new FieldStore<(ICarrierVisitAssignment | null)[]>([], carrierVisitAssignments =>
    carrierVisitAssignments.find(a => !a?.isDeleted && a?.visitId && !a.workAreaId)
      ? 'fieldIsRequired'
      : undefined,
  )
  visitIds = new FieldStore<number[]>([])
  isForMultipleEquipment = false

  constructor() {
    makeObservable(this, {
      isForMultipleEquipment: observable,

      canAddAssignment: computed,
      displayContent: computed,
      duplicatedAssignments: computed,

      setValues: action,
    })
  }

  setValues(assignments: ICarrierVisitAssignment[], isForMultipleEquipment: boolean) {
    this.isForMultipleEquipment = isForMultipleEquipment
    if (!isForMultipleEquipment) this.assignments.setValue(assignments)

    const uniqueVisitIds = assignments.reduce((acc: number[], assignment) => {
      if (assignment.visitId && !acc.includes(assignment.visitId)) {
        acc.push(assignment.visitId)
      }

      return acc
    }, [])

    this.visitIds.setValue(uniqueVisitIds)
  }

  addAssignment() {
    const newAssignments = [...this.assignments.value]

    newAssignments.push({
      filter: undefined,
      workAreaId: null,
      visitId: null,
      workAreas: [],
      isDeleted: false,
      carrierName: undefined,
    })

    this.assignments.setValue(newAssignments)
  }

  updateAssignment(
    index: number,
    visitId?: number | null,
    workAreaId?: number | null,
    workAreas: CarrierVisitPartialWorkAreaDto[] = [],
    carrierName: string | null | undefined = undefined,
  ) {
    const newAssignments = [...this.assignments.value]
    newAssignments[index] = {
      filter: undefined,
      workAreaId: workAreaId,
      visitId: visitId,
      workAreas: workAreas,
      isDeleted: false,
      carrierName: carrierName,
    }

    this.assignments.setValue(newAssignments)
  }

  removeAssignment(index: number) {
    const newAssignments = [...this.assignments.value]

    const assignment = newAssignments[index]
    if (assignment) {
      assignment.isDeleted = true
      this.assignments.setValue(newAssignments)
    }
  }

  clearVisitIds() {
    this.visitIds.setValue([])
  }

  validate = () => {
    return validateSubForm(this)
  }

  get containAssignments() {
    return this.assignments.value.some(a => a && a.isDeleted !== true)
  }

  get canAddAssignment() {
    return !this.containAssignments && !(this.isForMultipleEquipment && this.visitIds.value.length)
  }

  get displayContent() {
    return (
      this.containAssignments || (this.isForMultipleEquipment && this.visitIds.value.length > 0)
    )
  }

  get duplicatedAssignments() {
    const seen = new Set<number>()
    const duplicates = new Set<string>()

    for (const assignment of this.assignments.value) {
      if (!assignment || assignment.isDeleted || assignment.workAreaId == null) continue

      if (!seen.has(assignment.workAreaId)) {
        seen.add(assignment.workAreaId)
      } else if (assignment.carrierName) {
        duplicates.add(assignment.carrierName)
      }
    }

    return Array.from(duplicates)
  }

  get data() {
    return extractSubFormData(this) as ICarrierVisitAssignmentFormData
  }
}
